import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "motion/react";
import { MessageCircle, ChevronDown, Mail } from "lucide-react";
import { Navbar } from "./Navbar";
import { Footer } from "./Footer";

const contactChannels = [
  {
    title: "Email",
    description: "Kami akan membalas dalam 1 x 24 jam pada hari kerja.",
    value: "bandarmolony@gmail.com",
    href: "mailto:bandarmolony@gmail.com",
    icon: Mail,
  },
  {
    title: "WhatsApp",
    description: "Tersedia 09.00 - 17.00 WIB untuk konsultasi dengan kami.",
    value: "(+62) 813-2395-6278",
    href: "https://wa.me/6281323956278",
    icon: MessageCircle,
  },
];

const faqs = [
  {
    question: "What is BandarmoloNY?",
    answer: "BandarmoloNY is an advance market analysis platform for the Indonesian stock market, providing various analytical tools including Market Analysis, Broker Analysis, Stock Analysis, and much more to come.",
  },
  {
    question: "How do I start using the platform?",
    answer: "You can start by registering an account and selecting a suitable plan. We provide comprehensive plans with features tailored to your trading needs.",
  },
  {
    question: "Is there a free trial available?",
    answer: "Yes, you can try the platform for free. Start with a free trial to explore our complete features before subscribing.",
  },
];

export function ContactPage() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const toggleFaq = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="relative h-screen flex flex-col bg-gradient-to-br from-background via-muted/20 to-background text-foreground overflow-hidden">
      <Helmet>
        <title>Contact BandarmoloNY - Get Support & Assistance</title>
        <meta
          name="description"
          content="Contact BandarmoloNY support team via email at bandarmolony@gmail.com, WhatsApp at (+62) 817-370-486, or phone. Available 09:00 - 21:00 WIB for consultation and assistance."
        />
        <meta name="keywords" content="contact bandarmolony, support, customer service, trading platform support, indonesian stock market help" />
        <link rel="canonical" href="https://bandarmolony.com/contact" />
        <meta property="og:title" content="Contact BandarmoloNY - Support & Assistance" />
        <meta property="og:description" content="Get in touch with BandarmoloNY support team via email, WhatsApp, or phone. Available daily for consultation and trading assistance." />
        <meta property="og:url" content="https://bandarmolony.com/contact" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary" />
        <meta name="twitter:title" content="Contact BandarmoloNY" />
        <meta name="twitter:description" content="Contact our support team for trading platform assistance." />
      </Helmet>



      <div className="flex-shrink-0">
        <Navbar />
      </div>

      <main className="relative z-10 mx-auto flex flex-1 w-full max-w-5xl flex-col gap-6 px-6 pt-24 pb-8 md:pt-28 overflow-y-auto min-h-0">
        <motion.header
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-2 md:mt-4"
        >
          <h1 className="text-2xl font-bold tracking-tight md:text-3xl">Contact Us</h1>
          <p className="mx-auto mt-1 max-w-2xl text-sm leading-relaxed text-muted-foreground md:text-base">
            We are happy to help you
          </p>
        </motion.header>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch w-full mb-8">
          {/* Left Column: Contact Methods */}
          <section className="flex flex-col gap-3">
            {contactChannels.map(({ title, value, href, icon: Icon }, index) => (
              <motion.article
                key={title}
                initial={{ opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="flex w-full flex-col rounded-2xl border border-white/10 bg-background/80 px-5 py-4 shadow-xl backdrop-blur transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_0_35px_rgba(239,68,68,0.4)] dark:border-white/5"
              >
                <div className="flex items-center gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </div>
                  <h2 className="text-xl font-semibold text-foreground">{title}</h2>
                </div>
                <a
                  href={href}
                  className="mt-3 inline-flex items-center text-sm font-semibold text-primary underline-offset-4 transition hover:underline md:text-base"
                >
                  {value}
                </a>
              </motion.article>
            ))}
          </section>

          {/* Right Column: FAQ */}
          <motion.section
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6 }}
            className="rounded-2xl flex flex-col border border-white/10 bg-background/60 px-4 py-3 backdrop-blur dark:border-white/5 md:px-5 md:py-3"
          >
            <h2 className="text-xl font-semibold text-center md:text-2xl">Frequently Asked Questions (FAQ)</h2>
            <div className="mt-2 space-y-2 flex-1 flex flex-col justify-evenly">
              {faqs.map((faq, index) => (
                <div key={index} className="rounded-lg bg-background/40 overflow-hidden border border-white/5">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-background/20 transition-colors"
                  >
                    <h3 className="font-semibold text-sm md:text-base text-foreground">{faq.question}</h3>
                    <ChevronDown
                      className={`w-5 h-5 text-muted-foreground transition-transform duration-200 ${openIndex === index ? 'rotate-180' : ''
                        }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ${openIndex === index ? 'max-h-96' : 'max-h-0'
                      }`}
                  >
                    <p className="px-3 pb-3 text-sm text-muted-foreground">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </motion.section>
        </div>
      </main>

      {/* SEO Backlinks Hidden Section */}
      <div className="sr-only">
        <h2>Contact BandarmoloNY Team</h2>
        <nav>
          <ul>
            <li><a href="https://www.hintstechnology.com/" rel="nofollow noopener noreferrer">Hints Technology - Contact Development Team</a></li>
            <li><a href="https://bandarmolony.com/" rel="nofollow noopener noreferrer">BandarmoloNY Trading Platform</a></li>
            <li><a href="https://bandarmolony.com/features" rel="nofollow noopener noreferrer">Platform Features</a></li>
            <li><a href="https://bandarmolony.com/pricing" rel="nofollow noopener noreferrer">View Pricing Plans</a></li>
          </ul>
        </nav>
        <p>Get in touch with Hints Technology for trading platform support and inquiries about BandarmoloNY.</p>
      </div>

      <div className="flex-shrink-0">
        <Footer />
      </div>
    </div>
  );
}
